<template>
  <div class="about-page page-body">
    <div class="container">
      <PageHero
        ribbon="About Gaoren"
        :title="'关于' + company.shortName"
        :subtitle="company.tagline"
        :breadcrumbs="[
          { label: '首页', to: '/' },
          { label: '关于我们' },
        ]"
      />

      <section class="intro-section">
        <div class="intro-grid">
        <div class="intro-copy" data-reveal>
          <span class="section-eyebrow">Company Profile</span>
          <h2 class="section-title">公司简介</h2>
          <p>{{ companyIntro.summary }}</p>
          <p>{{ companyIntro.vision }}</p>
          <ul class="value-tags">
            <li v-for="v in companyIntro.values" :key="v">{{ v }}</li>
          </ul>
        </div>
        <div class="intro-visual" data-reveal data-reveal-delay="2">
          <img src="/images/pdf-pages/gaoren-02.png" alt="天津高仁科技办公环境" />
          <div class="intro-badge">
            <strong><CountUpNumber :value="Number(company.founded)" /></strong>
            <span>创建于天津</span>
          </div>
        </div>
        </div>
      </section>
    </div>

    <section class="stats-section">
      <div class="container stats-grid">
        <article v-for="(stat, idx) in stats" :key="stat.label" data-reveal :data-reveal-delay="String(idx + 1)">
          <strong>
            <CountUpNumber
              v-if="stat.value != null"
              :value="stat.value"
              :suffix="stat.suffix || ''"
            />
            <template v-else>{{ stat.text }}</template>
          </strong>
          <span>{{ stat.label }}</span>
        </article>
      </div>
    </section>

    <section class="timeline-section">
      <div class="container">
        <div class="section-head" data-reveal>
          <span class="section-eyebrow">Milestones</span>
          <h2 class="section-title">发展历程</h2>
        </div>
        <div class="timeline">
          <article
            v-for="(item, idx) in milestones"
            :key="item.year"
            class="timeline-item"
            data-reveal
            :data-reveal-delay="String(idx % 4 + 1)"
          >
            <span class="timeline-item__year">{{ item.year }}</span>
            <h3>{{ item.title }}</h3>
            <p>{{ item.desc }}</p>
          </article>
        </div>
      </div>
    </section>

    <section class="capability-section">
      <div class="container">
        <div class="section-head" data-reveal>
          <span class="section-eyebrow">Core Capabilities</span>
          <h2 class="section-title">核心业务方向</h2>
        </div>
        <div class="capability-grid">
          <article
            v-for="(cap, idx) in companyIntro.capabilities"
            :key="cap"
            class="capability-card"
            data-reveal
            :data-reveal-delay="String(idx + 1)"
          >
            <span class="capability-card__num">{{ String(idx + 1).padStart(2, '0') }}</span>
            <p>{{ cap }}</p>
          </article>
        </div>
      </div>
    </section>

    <section class="clients-section">
      <div class="container">
        <div class="section-head" data-reveal>
          <span class="section-eyebrow">Trusted By</span>
          <h2 class="section-title">主要服务客户</h2>
        </div>
        <div class="clients-grid">
          <div
            v-for="group in clientGroups"
            :key="group.label"
            class="client-group"
            data-reveal
          >
            <h3>{{ group.label }}</h3>
            <ul>
              <li v-for="client in group.clients" :key="client">{{ client }}</li>
            </ul>
          </div>
        </div>
        <div class="clients-visual" data-reveal>
          <img src="/images/brochure/img-p36-2.jpeg" alt="服务客户名录" />
        </div>
      </div>
    </section>

    <section class="contact-cta">
      <div class="container contact-cta__inner" data-reveal>
        <div>
          <h2>期待与您合作</h2>
          <p>欢迎来电咨询或到访天津南开华创大厦</p>
        </div>
        <div class="contact-cta__actions">
          <a :href="`tel:${company.phone}`" class="btn btn--primary">{{ company.phone }}</a>
          <router-link :to="{ name: 'home', query: { section: 'consult' } }" class="btn btn--secondary">
            在线留言
          </router-link>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import PageHero from '../components/PageHero.vue'
import CountUpNumber from '../components/CountUpNumber.vue'
import { company, companyIntro, milestones, clientGroups } from '../data/siteConfig'
import { useScrollReveal } from '../composables/useScrollReveal'

useScrollReveal()

const stats = [
  { value: 5, suffix: '+', label: '大类产品线' },
  { value: 30, suffix: '+', label: '细分产品分类' },
  { value: 2, label: '自研云平台' },
  { value: null, text: '全国', label: '服务覆盖范围' },
]
</script>

<style scoped>
.about-page {
  min-height: 60vh;
}

.intro-section {
  padding: 32px 0 48px;
}

.page-hero__bg img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  animation: kenBurns 18s ease-in-out infinite alternate;
}

.page-hero__overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(12, 28, 64, 0.88) 0%, rgba(43, 89, 195, 0.65) 100%);
}

.page-hero__inner {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 48px 24px 56px;
  color: #fff;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  margin-bottom: 20px;
  opacity: 0.85;
}

.breadcrumb a {
  color: #fff;
  text-decoration: none;
}

.breadcrumb a:hover {
  text-decoration: underline;
}

.page-hero h1 {
  margin: 0 0 12px;
  font-size: 42px;
  font-weight: 700;
  letter-spacing: 1px;
}

.page-hero p {
  margin: 0;
  font-size: 18px;
  opacity: 0.9;
}

.intro-section {
  padding: 80px 0;
}

.intro-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 56px;
  align-items: center;
}

.intro-copy p {
  margin: 0 0 16px;
  font-size: 15px;
  line-height: 1.85;
  color: var(--text-secondary);
}

.value-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: 24px 0 0;
  padding: 0;
  list-style: none;
}

.value-tags li {
  padding: 8px 18px;
  background: linear-gradient(135deg, rgba(43, 89, 195, 0.08), rgba(43, 89, 195, 0.04));
  border: 1px solid rgba(43, 89, 195, 0.15);
  border-radius: 24px;
  font-size: 13px;
  font-weight: 600;
  color: var(--brand-primary);
}

.intro-visual {
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: var(--shadow-lg);
}

.intro-visual img {
  width: 100%;
  display: block;
}

.intro-badge {
  position: absolute;
  bottom: 24px;
  left: 24px;
  padding: 16px 24px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 12px;
  backdrop-filter: blur(8px);
}

.intro-badge strong {
  display: block;
  font-size: 32px;
  color: var(--brand-primary);
  line-height: 1;
}

.intro-badge span {
  font-size: 13px;
  color: var(--text-muted);
}

.stats-section {
  padding: 48px 0;
  background: linear-gradient(135deg, #1a2d5a 0%, var(--brand-primary) 100%);
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  text-align: center;
  color: #fff;
}

.stats-grid strong {
  display: block;
  font-size: 40px;
  font-weight: 700;
  margin-bottom: 8px;
}

.stats-grid span {
  font-size: 14px;
  opacity: 0.85;
}

.timeline-section {
  padding: 80px 0;
  background: var(--surface-muted);
}

.timeline {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 24px;
  margin-top: 40px;
}

.timeline-item {
  padding: 28px;
  background: var(--surface-card);
  border-radius: var(--radius-lg);
  border-left: 4px solid var(--brand-primary);
  box-shadow: var(--shadow-sm);
  transition: transform 0.3s var(--ease-smooth), box-shadow 0.3s;
}

.timeline-item:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-md);
}

.timeline-item__year {
  display: inline-block;
  padding: 4px 12px;
  background: rgba(43, 89, 195, 0.1);
  color: var(--brand-primary);
  font-size: 13px;
  font-weight: 700;
  border-radius: 4px;
  margin-bottom: 12px;
}

.timeline-item h3 {
  margin: 0 0 8px;
  font-size: 17px;
}

.timeline-item p {
  margin: 0;
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.6;
}

.capability-section {
  padding: 80px 0;
}

.capability-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-top: 40px;
}

.capability-card {
  padding: 28px;
  background: var(--surface-card);
  border: 1px solid var(--border-soft);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
  transition: border-color 0.3s, box-shadow 0.3s, transform 0.3s;
}

.capability-card:hover {
  border-color: rgba(13, 79, 110, 0.2);
  box-shadow: var(--shadow-md);
  transform: translateY(-3px);
}

.capability-card__num {
  display: block;
  font-size: 28px;
  font-weight: 700;
  color: rgba(43, 89, 195, 0.2);
  margin-bottom: 12px;
}

.capability-card p {
  margin: 0;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.5;
}

.clients-section {
  padding: 80px 0;
  background: var(--surface-muted);
}

.clients-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  margin-top: 40px;
}

.client-group {
  padding: 24px;
  background: #fff;
  border-radius: 12px;
  box-shadow: var(--shadow-sm);
}

.client-group h3 {
  margin: 0 0 16px;
  font-size: 15px;
  color: var(--brand-primary);
  padding-bottom: 12px;
  border-bottom: 2px solid rgba(43, 89, 195, 0.15);
}

.client-group ul {
  margin: 0;
  padding: 0;
  list-style: none;
}

.client-group li {
  padding: 6px 0;
  font-size: 13px;
  color: var(--text-secondary);
  border-bottom: 1px dashed #eee;
}

.client-group li:last-child {
  border-bottom: none;
}

.clients-visual {
  margin-top: 40px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: var(--shadow-md);
}

.clients-visual img {
  width: 100%;
  display: block;
}

.contact-cta {
  padding: 64px 0;
  background: linear-gradient(135deg, #0f1f3d 0%, #1a3568 100%);
}

.contact-cta__inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 32px;
  color: #fff;
}

.contact-cta h2 {
  margin: 0 0 8px;
  font-size: 28px;
}

.contact-cta p {
  margin: 0;
  opacity: 0.85;
}

.contact-cta__actions {
  display: flex;
  gap: 16px;
  flex-shrink: 0;
}

@media (max-width: 960px) {
  .intro-grid,
  .stats-grid,
  .capability-grid,
  .clients-grid {
    grid-template-columns: 1fr 1fr;
  }
  .contact-cta__inner {
    flex-direction: column;
    text-align: center;
  }
}

@media (max-width: 640px) {
  .page-hero h1 {
    font-size: 28px;
  }
  .intro-grid,
  .stats-grid,
  .capability-grid,
  .clients-grid {
    grid-template-columns: 1fr;
  }
}
</style>
